import React, { useState, useRef, useEffect, KeyboardEvent } from 'react'
import ReactDOM from 'react-dom'
import { pantryColors } from '@pantry/ui'

interface SuggestionItem {
  id: number
  name: string
}

interface ChipInputProps {
  value: string[]
  onChange: (tags: string[]) => void
  placeholder?: string
  chipColor?: 'brand' | 'amber' | 'purple' | 'blue' | 'gray'
  disabled?: boolean
  suggestions?: SuggestionItem[]
  onSelectSuggestion?: (suggestion: SuggestionItem) => void
  /**
   * Optional controlled value for the typing-in-progress text. When defined,
   * ChipInput acts as a controlled component for the input field — the parent
   * owns the text and can reset it (e.g. to clear after launching a + Create
   * affordance, so the text isn't left over to become a duplicate free-text chip
   * on the next blur).
   */
  inputValue?: string
  onInputChange?: (text: string) => void
  'aria-label'?: string
  // Optional caps (e.g. to match backend list limits). Absent → unlimited.
  maxItems?: number
  maxLength?: number
}

const COLOR_MAP = {
  brand: { bg: pantryColors.brand.light, text: pantryColors.brand.primary },
  amber: { bg: pantryColors.amber[100], text: pantryColors.amber[700] },
  purple: { bg: pantryColors.purple[50], text: pantryColors.purple[700] },
  blue: { bg: pantryColors.blue[50], text: pantryColors.blue[700] },
  gray: { bg: pantryColors.gray[100], text: pantryColors.gray[700] },
}

interface DropdownCoords {
  top: number
  left: number
  width: number
}

export const ChipInput: React.FC<ChipInputProps> = ({
  value,
  onChange,
  placeholder = 'Add tag…',
  chipColor = 'brand',
  disabled = false,
  suggestions = [],
  onSelectSuggestion,
  inputValue: inputValueProp,
  onInputChange,
  'aria-label': ariaLabel,
  maxItems,
  maxLength,
}) => {
  const [internalInputVal, setInternalInputVal] = useState('')
  const isControlled = inputValueProp !== undefined
  const inputVal = isControlled ? inputValueProp : internalInputVal
  const setInputVal = (next: string) => {
    if (!isControlled) setInternalInputVal(next)
    onInputChange?.(next)
  }
  const [activeIndex, setActiveIndex] = useState(-1)
  const [coords, setCoords] = useState<DropdownCoords | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const colors = COLOR_MAP[chipColor]
  const atLimit = maxItems !== undefined && value.length >= maxItems

  const filteredSuggestions = inputVal.trim()
    ? suggestions.filter(
        (s) =>
          s.name.toLowerCase().includes(inputVal.toLowerCase()) &&
          !value.includes(s.name),
      )
    : []

  const showDropdown = filteredSuggestions.length > 0

  const recalcCoords = () => {
    if (!wrapperRef.current) return
    const r = wrapperRef.current.getBoundingClientRect()
    setCoords({ top: r.bottom + 4, left: r.left, width: r.width })
  }

  useEffect(() => {
    if (!showDropdown) {
      setCoords(null)
      return
    }
    recalcCoords()
    window.addEventListener('scroll', recalcCoords, true)
    window.addEventListener('resize', recalcCoords)
    return () => {
      window.removeEventListener('scroll', recalcCoords, true)
      window.removeEventListener('resize', recalcCoords)
    }
  }, [showDropdown])

  useEffect(() => {
    setActiveIndex(-1)
  }, [inputVal])

  const addFreeText = (text: string) => {
    const trimmed = maxLength !== undefined ? text.trim().slice(0, maxLength) : text.trim()
    if (trimmed && !atLimit && !value.includes(trimmed)) {
      onChange([...value, trimmed])
    }
    setInputVal('')
  }

  const selectSuggestion = (suggestion: SuggestionItem) => {
    if (!atLimit) onSelectSuggestion?.(suggestion)
    setInputVal('')
  }

  const removeTag = (tag: string) => {
    onChange(value.filter((t) => t !== tag))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (showDropdown) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((prev) => Math.min(prev + 1, filteredSuggestions.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((prev) => Math.max(prev - 1, -1))
        return
      }
      if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault()
        const chosen = filteredSuggestions[activeIndex]
        if (chosen) selectSuggestion(chosen)
        return
      }
      if (e.key === 'Escape') {
        // Only close the list — keep the Esc from reaching an enclosing dialog.
        e.preventDefault()
        e.stopPropagation()
        setInputVal('')
        return
      }
    }

    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addFreeText(inputVal)
    } else if (e.key === 'Backspace' && inputVal === '' && value.length > 0) {
      onChange(value.slice(0, -1))
    }
  }

  const dropdown =
    showDropdown && coords
      ? ReactDOM.createPortal(
          <ul
            ref={listRef}
            role="listbox"
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              width: coords.width,
              background: pantryColors.white,
              border: `1px solid ${pantryColors.gray[200]}`,
              borderRadius: 8,
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              maxHeight: 220,
              overflowY: 'auto',
              margin: 0,
              padding: 0,
              listStyle: 'none',
              zIndex: 1300,
            }}
          >
            {filteredSuggestions.map((s, idx) => (
              <li
                key={s.id}
                role="option"
                aria-selected={idx === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault()
                  selectSuggestion(s)
                }}
                onMouseEnter={() => setActiveIndex(idx)}
                style={{
                  padding: '8px 12px',
                  fontSize: 13,
                  cursor: 'pointer',
                  color: pantryColors.gray[700],
                  background: idx === activeIndex ? pantryColors.brand.light : 'transparent',
                  borderBottom:
                    idx < filteredSuggestions.length - 1
                      ? `1px solid ${pantryColors.gray[100]}`
                      : 'none',
                }}
              >
                {s.name}
              </li>
            ))}
          </ul>,
          document.body,
        )
      : null

  return (
    <>
      <div ref={wrapperRef}>
        <div
          onClick={() => {
            if (!disabled) inputRef.current?.focus()
          }}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 5,
            alignItems: 'center',
            border: `1px solid ${pantryColors.gray[200]}`,
            borderRadius: 8,
            padding: '6px 9px',
            minHeight: 32,
            cursor: disabled ? 'default' : 'text',
            background: disabled ? pantryColors.gray[50] : pantryColors.white,
            transition: 'border-color .12s, box-shadow .12s',
          }}
          onFocus={(e) => {
            if (e.currentTarget.contains(e.target)) {
              e.currentTarget.style.borderColor = pantryColors.brand.primary
              e.currentTarget.style.boxShadow = `0 0 0 3px rgba(37,99,235,.1)`
            }
          }}
          onBlur={(e) => {
            if (
              !e.currentTarget.contains(e.relatedTarget) &&
              !listRef.current?.contains(e.relatedTarget as Node)
            ) {
              e.currentTarget.style.borderColor = pantryColors.gray[200]
              e.currentTarget.style.boxShadow = 'none'
            }
          }}
          aria-label={ariaLabel}
        >
          {value.map((tag) => (
            <span
              key={tag}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                background: colors.bg,
                color: colors.text,
                fontSize: 12,
                fontWeight: 500,
                padding: '3px 9px',
                borderRadius: 999,
              }}
            >
              {tag}
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    removeTag(tag)
                  }}
                  aria-label={`Remove ${tag}`}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'inherit',
                    fontSize: 13,
                    lineHeight: 1,
                    padding: 0,
                    opacity: 0.6,
                    marginLeft: 1,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.6'
                  }}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {!disabled && (
            <input
              ref={inputRef}
              type="text"
              value={inputVal}
              maxLength={maxLength}
              readOnly={atLimit}
              onChange={(e) => setInputVal(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={(e) => {
                if (listRef.current?.contains(e.relatedTarget as Node)) return
                if (inputVal.trim()) addFreeText(inputVal)
              }}
              placeholder={value.length === 0 ? placeholder : ''}
              aria-autocomplete="list"
              aria-expanded={showDropdown}
              style={{
                border: 'none',
                outline: 'none',
                fontSize: 13,
                fontFamily: 'inherit',
                background: 'transparent',
                minWidth: 80,
                color: pantryColors.gray[800],
                padding: '2px 3px',
              }}
            />
          )}
        </div>
        {atLimit && !disabled && (
          <div role="status" style={{ fontSize: 11, color: pantryColors.gray[400], marginTop: 4 }}>
            Maximum of {maxItems} reached
          </div>
        )}
      </div>
      {dropdown}
    </>
  )
}
